import { Button, column, Css, GridColumn, GridDataRow, GridTable, simpleHeader } from "@homebound/beam";
import { useSuspense } from "@rest-hooks/react";
import { useState } from "react";
import { useController } from "src/hooks/useController";
import { UW_CONFIG_HISTORY_PAGE_SIZE, UwConfigHistoryEndpoint, UwConfigHistoryEntry } from "./UwConfigEndpoints";
import { changeValue } from "./UwConfigImportExport";
import { formatDate } from "./UwConfigRowModal";
import { uwConfigTypeDef } from "./uwConfigFields";

/** Every change to every config, newest first */
export function UwConfigHistory() {
  const { changes: firstPage } = useSuspense(UwConfigHistoryEndpoint, {});
  const { fetch } = useController();
  const [olderPages, setOlderPages] = useState<UwConfigHistoryEntry[]>([]);
  const [hasMore, setHasMore] = useState(firstPage.length === UW_CONFIG_HISTORY_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const entries = [...firstPage, ...olderPages];

  async function loadMore() {
    setLoading(true);
    try {
      const { changes } = await fetch(UwConfigHistoryEndpoint, { beforeId: entries[entries.length - 1].id });
      setOlderPages((pages) => [...pages, ...changes]);
      setHasMore(changes.length === UW_CONFIG_HISTORY_PAGE_SIZE);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div css={Css.dg.gap2.$}>
      <GridTable columns={columns} rows={createRows(entries)} stickyHeader fallbackMessage="No changes yet" />
      {hasMore && (
        <div>
          <Button variant="secondary" label="Load More" onClick={loadMore} disabled={loading} />
        </div>
      )}
    </div>
  );
}

type Row = { kind: "header" } | { kind: "data"; data: UwConfigHistoryEntry };

function createRows(entries: UwConfigHistoryEntry[]): GridDataRow<Row>[] {
  return [simpleHeader, ...entries.map((e) => ({ kind: "data" as const, id: `${e.id}`, data: e }))];
}

const actionLabels: Record<UwConfigHistoryEntry["action"], string> = {
  create: "Added",
  update: "Changed",
  delete: "Deleted",
};

const columns: GridColumn<Row>[] = [
  column<Row>({ header: "When", data: ({ created_at }) => formatDate(created_at), w: "170px" }),
  column<Row>({ header: "Who", data: ({ user }) => user ?? "", w: "220px" }),
  column<Row>({
    header: "Config",
    data: ({ config_type }) => uwConfigTypeDef(config_type)?.label ?? config_type,
    w: "160px",
  }),
  column<Row>({ header: "Action", data: ({ action }) => actionLabels[action], w: "90px" }),
  column<Row>({ header: "Row", data: ({ row_label }) => row_label, w: 2 }),
  column<Row>({ header: "Field", data: ({ field }) => field ?? "", w: 1 }),
  column<Row>({ header: "Before", data: ({ old_value }) => changeValue(old_value), w: 1 }),
  column<Row>({ header: "After", data: ({ new_value }) => changeValue(new_value), w: 1 }),
];
